import { log } from '@/logger/logger.ts';
import { withRetry } from '@/utils/async.utils.ts';
import { resolveFontFileUrl } from '@/utils/fontAssetUtils.ts';

const logger = log.getLogger('Wasm');

// Generic loading state and cache management
const loadingPromises = new Map<string, Promise<unknown>>();

interface LoaderConfig<T> {
  loader: (url: string) => Promise<T>;
}

/**
 * Generic file loader with caching and promise deduplication
 */
async function loadFile<T>(url: string, config: LoaderConfig<T>): Promise<T> {
  const cacheKey = url;

  // Check if resource is currently being loaded
  if (loadingPromises.has(cacheKey)) {
    return loadingPromises.get(cacheKey) as Promise<T>;
  }

  // Create new loading promise
  const loadingPromise = withRetry(() => config.loader(url))
    .then((result) => {
      loadingPromises.delete(cacheKey);
      return result;
    })
    .catch((error) => {
      loadingPromises.delete(cacheKey);
      throw error;
    });

  loadingPromises.set(cacheKey, loadingPromise);
  return loadingPromise;
}

// Specific loader implementations
function createImageLoader(url: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image: ' + url));
    img.src = url;
  });
}

function createVideoLoader(url: string): Promise<HTMLVideoElement> {
  const video = document.createElement('video');
  video.crossOrigin = 'Anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.loop = true;
  video.autoplay = false;
  video.src = url;
  return Promise.resolve(video);
}

function createThreeDModelLoader(url: string): Promise<Uint8Array> {
  return fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      return res.bytes();
    })
    .catch((error) => {
      throw new Error('Failed to load 3D model: ' + url + (error.message ? ': ' + error.message : ''));
    });
}

async function createFontLoader(resourceKey: string, workspaceCustomFontsUrlsMap: ReadonlyMap<string, string>) {
  try {
    const url = resolveFontFileUrl(resourceKey, workspaceCustomFontsUrlsMap);
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    return res.bytes();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error('Failed to load font: ' + resourceKey + (message ? ': ' + message : ''));
  }
}

export async function loadImageResources(
  imagesToLoad: string[],
  callback: (url: string, img: HTMLImageElement | undefined) => void,
) {
  await Promise.all(
    imagesToLoad.map(async (imageUrl) => {
      let img: HTMLImageElement | undefined = undefined;
      try {
        img = await loadFile(imageUrl, {
          loader: createImageLoader,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load image resource: ' + imageUrl + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load image resource: ' + imageUrl);
        }
      }
      callback(imageUrl, img);
    }),
  );
}

export async function loadVideoResources(
  urlsToLoad: string[],
  callback: (url: string, video: HTMLVideoElement | undefined) => void,
) {
  await Promise.all(
    urlsToLoad.map(async (url) => {
      let video: HTMLVideoElement | undefined = undefined;
      try {
        video = await loadFile(url, {
          loader: createVideoLoader,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load video resource: ' + url + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load video resource: ' + url);
        }
      }
      callback(url, video);
    }),
  );
}

export async function loadThreeDModelResources(
  urlsToLoad: string[],
  callback: (url: string, modelBytes: Uint8Array | undefined) => void,
) {
  await Promise.all(
    urlsToLoad.map(async (url) => {
      let modelBytes: Uint8Array | undefined = undefined;
      try {
        modelBytes = await loadFile(url, {
          loader: createThreeDModelLoader,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load 3D model resource: ' + url + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load 3D model resource: ' + url);
        }
      }
      callback(url, modelBytes);
    }),
  );
}

export async function loadFontResources(
  resourceKeysToLoad: string[],
  callback: (resourceKey: string, fontBytes: Uint8Array | undefined) => void,
  workspaceCustomFontsUrlsMap: ReadonlyMap<string, string>,
) {
  await Promise.all(
    resourceKeysToLoad.map(async (resourceKey) => {
      let fontBytes: Uint8Array | undefined = undefined;
      try {
        fontBytes = await loadFile(resourceKey, {
          loader: (key) => createFontLoader(key, workspaceCustomFontsUrlsMap),
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load font resource: ' + resourceKey + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load font resource: ' + resourceKey);
        }
      }
      callback(resourceKey, fontBytes);
    }),
  );
}
